import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getApprovals } from "../../services/incentiveService";
import { getCompanySalesReps } from "../../services/userService";
import { SCROLL_CONFIG } from "../../utils/constants";
import useThrottledScroll from "../../hooks/useThrottledScroll";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import Table from "../../shared/components/Table/Table";
import ResultsLoadMorePanel from "../../shared/components/ResultsLoadMorePanel/ResultsLoadMorePanel";
import "./Incentive.scss";

/**
 * Incentive Component - Displays sales agents and their approval counts
 * Updates URL when company changes and fetches data accordingly
 * Access controlled by permissions.incentive.view in Dashboard routes
 */
const Incentive = ({ currentCompany, userInfo, permissions = {} }) => {
  // Message API from MessageProvider for toast notifications
  const message = useMessageApi();

  // State variables to manage component data and UI
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [approvals, setApprovals] = useState(null);
  const [agents, setAgents] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(SCROLL_CONFIG.INITIAL_LOAD_SIZE);
  const [hasMoreData, setHasMoreData] = useState(true);

  // Get location and navigate hooks
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get company from URL or use provided company
  const urlCompany = new URLSearchParams(location.search).get("company");
  const company = urlCompany || currentCompany;

  // Sync URL with currentCompany prop changes
  // This ensures the URL always reflects the current company selection
  useEffect(() => {
    if (currentCompany && currentCompany !== urlCompany) {
      // Update URL with new company parameter
      navigate(`/dashboard/incentive?company=${encodeURIComponent(currentCompany)}`, { 
        replace: true // Use replace to avoid creating multiple history entries
      });
    }
  }, [currentCompany, urlCompany, navigate]);

  // Fetch data when component mounts or company changes
  useEffect(() => {
    // Only fetch if company is available
    if (!company) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setDisplayedCount(SCROLL_CONFIG.INITIAL_LOAD_SIZE);
      setHasMoreData(true);
      
      try {
        // Fetch approvals data and company sales reps in parallel
        const [approval, agentsData] = await Promise.all([
          getApprovals(company, {}, message),
          getCompanySalesReps(company)
        ]);

        // Update state with fetched data
        setApprovals(approval);
        setAgents(agentsData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [company, message]);

  // Process and prepare table data
  const allTableData = useMemo(() => {
    if (!agents || !approvals) return [];

    // Create a map to count approvals for each agent
    const approvalsMap = new Map();
    approvals.forEach((lead) => {
      // Count approvals for both Sales Agent and Account Executive
      approvalsMap.set(
        lead.Sales_Agent,
        (approvalsMap.get(lead.Sales_Agent) || 0) + 1
      );
      approvalsMap.set(
        lead.Account_Executive,
        (approvalsMap.get(lead.Account_Executive) || 0) + 1
      );
    });

    // Transform agent data into table format and sort by approval count
    return agents
      .map((agent) => ({
        id: agent.id || `${agent.name}-${Math.random().toString(36).substr(2, 9)}`,
        businessName: agent.name,
        salesRep: approvalsMap.get(agent.name) || 0,
      }))
      .sort((a, b) => b.salesRep - a.salesRep);
  }, [agents, approvals]);

  // Get data for current display count (infinite scroll)
  const displayedData = useMemo(() => {
    return allTableData.slice(0, displayedCount);
  }, [allTableData, displayedCount]);

  // Check if there's more data to load
  useEffect(() => {
    setHasMoreData(displayedCount < allTableData.length);
  }, [displayedCount, allTableData.length]);

  // Define table column headers
  const headers = ["Sales Agent", "Apps Approved"];

  // Load more data when scrolling near bottom
  const loadMoreData = useCallback(() => {
    if (!isLoadingMore && hasMoreData) {
      setIsLoadingMore(true);
      
      // Simulate a small delay for better UX
      setTimeout(() => {
        setDisplayedCount(prev => Math.min(prev + SCROLL_CONFIG.LOAD_MORE_SIZE, allTableData.length));
        setIsLoadingMore(false);
      }, 300);
    }
  }, [isLoadingMore, hasMoreData, allTableData.length]);


  // Handle browser scroll events with throttling
  const handleBrowserScroll = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_CONFIG.SCROLL_THRESHOLD;
    
    if (isNearBottom && hasMoreData && !isLoadingMore) {
      loadMoreData();
    }
  }, [hasMoreData, isLoadingMore, loadMoreData]);

  // Create throttled scroll handler
  const throttledBrowserScrollHandler = useThrottledScroll(handleBrowserScroll, SCROLL_CONFIG.THROTTLE_DELAY);

  // Handle browser scroll events instead of container scroll
  useEffect(() => {
    window.addEventListener('scroll', throttledBrowserScrollHandler);
    
    return () => {
      window.removeEventListener('scroll', throttledBrowserScrollHandler);
    };
  }, [throttledBrowserScrollHandler]);

  const handleLoadMoreClick = useCallback(
    (shouldScrollAfterLoad = true) => {
      loadMoreData();

      if (shouldScrollAfterLoad) {
        setTimeout(() => {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
          });
        }, 100);
      }
    },
    [loadMoreData]
  );

  // Render component
  return (
    <div className="incentive-container">
      <section className="db-inner">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 pt-3">
              {/* Display table with browser scroll */}
              <Table
                merchants={displayedData}
                headers={headers}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                hasMore={hasMoreData}
                onLoadMore={loadMoreData}
                hideFooter={true}
              />

            </div>
          </div>
        </div>
      </section>
      
      <ResultsLoadMorePanel
        isLoadingMore={isLoadingMore}
        hasMore={hasMoreData}
        onLoadMore={handleLoadMoreClick}
        loading={isLoading}
        itemsCount={displayedData.length}
      />
    </div>
  );
};

export default Incentive;
